/* eslint-disable unicorn/prefer-ternary */
/* eslint-disable unicorn/explicit-length-check */
import { createAction } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import type { NgPost } from "../format-post";

import { parseFormDataToPost, parseNgDataToPost } from "../format-post";
import { getErrorMessage } from "../../../utils/get-error-message";
import { getScheduleSubmitErrorMessage } from "../../../utils/schedule-submit-error";
import {
  createScheduleAttemptId,
  forgetScheduleAttempt,
  reuseAttemptCover,
} from "../../../api/schedule-idempotency";
import { put, putResolve, select, take } from "redux-saga/effects";
import { startPostForm } from "../../../actions/postForm";
import { validateFull } from "../../../components/Posts/PostForm/postFormValidator";
import type { IgPreflightResult } from "../../../api/post";
import {
  SAVE_POST_FORM_ROUTINE,
  UPDATE_POST_FORM_VALIDATION,
} from "../../../constants/ActionTypes";

import { closePostForm, isFormSubmitting } from "../post-form.model";
import { Post } from "../types";
import { savePost } from "../post.model";
import {
  editingPostNeedUploadCover,
  getUpdateThumbnail,
  normalizePostMediaShape,
  syncCoverToMediaFiles,
} from "../utils";
import { ReduxRootState } from "../../../types/redux-store";
import { AccountType } from "../../../shared/types/accounts";
import {
  IOverlapSchedule,
  SaveFormResult,
} from "../../../components/Posts/PostForm/types";
import { getUser } from "../../../selectors/commonSelectors";
import { recordIfFacebookStory } from "../../../components/Posts/PostForm/FbStoryPromo/fb-story-promo";

export const startPostEditing = createAction("START_POST_EDITING");

export function* PostEditing(
  post: Post,
  {
    onSubmit,
  }: {
    onSubmit: (
      newPost: Post | Post[],
      previousPost: Post,
      scheduleAttemptId: string
    ) => void;
  }
): Generator<unknown, SaveFormResult | undefined, any> {
  const allAccounts: ReturnType<typeof allAccountsSelector> =
    yield select(allAccountsSelector);

  const user = yield select(getUser);
  const userTimezone: string = user?.timezone || "UTC";

  const accounts: AccountType[] = yield select(
    getAccountsByIds(
      (post.accountIds || []).length ? post.accountIds : [post.accountId]
    )
  );

  yield put(
    startPostForm(
      {
        ...post,
        accountIds: accounts.map((account) => account._id),
      },
      accounts
    )
  );

  let scheduleAttemptId: string | undefined;
  while (true) {
    const { payload: formData }: { payload: SaveFormResult } = yield take(
      closePostForm.type
    );
    if (!formData) {
      if (scheduleAttemptId) forgetScheduleAttempt(scheduleAttemptId);
      break;
    }

    const overlapSchedule: IOverlapSchedule = yield select(
      (state: ReduxRootState) =>
        state.forms.postForms.byId[post._id]?.overlapSchedule
    );

    /*
     * The same preflight answer the live form validated against. Without it a
     * blocker the customer can see would not refuse the save, and the message
     * would vanish the moment they pressed the button.
     */
    const igPreflight: IgPreflightResult | undefined = yield select(
      (state: ReduxRootState) =>
        state.forms.postForms.byId[post._id]?.igPreflight
    );

    const [errors, warnings] = validateFull(
      formData.newPostState,
      formData.post,
      formData.platformFields,
      formData.platforms,
      overlapSchedule,
      userTimezone,
      igPreflight
    );

    if (errors && Object.keys(errors).length > 0) {
      yield put({
        type: UPDATE_POST_FORM_VALIDATION,
        payload: { postId: post["_id"], errors, warnings },
      });
      yield put({
        type: SAVE_POST_FORM_ROUTINE.FAILURE,
        error: false,
        payload: "",
      });
      continue;
    }

    try {
      yield put(isFormSubmitting.actions.startFormSubmitting());

      scheduleAttemptId ??= createScheduleAttemptId();
      const { newPost, previousPost } = yield processPostFormUpdate({
        post,
        formData,
        allAccounts,
        userTimezone,
        scheduleAttemptId,
      });

      yield onSubmit(newPost, previousPost, scheduleAttemptId);
      forgetScheduleAttempt(scheduleAttemptId);
      recordIfFacebookStory(formData.post);
      toast.success("Posts submitted successfully");
      return formData;
    } catch (error) {
      const errorMsg =
        getScheduleSubmitErrorMessage(error) ?? getErrorMessage(error);
      yield put({
        type: SAVE_POST_FORM_ROUTINE.FAILURE,
        error: true,
        payload: {
          message: errorMsg,
          postId: post._id,
        },
      });
      toast.error(errorMsg);
    } finally {
      yield put(isFormSubmitting.actions.finishFormSubmitting());
    }
  }

  return undefined;
}

export function* editPost(
  post: Post,
  onSubmit: ((post: Post | Post[]) => Post) | undefined
): Generator<unknown, SaveFormResult | undefined, any> {
  const accounts: AccountType[] = yield select(
    getAccountsByIds(post.accountIds || [post.accountId])
  );

  return yield PostEditing(
    {
      ...post,
      // remove all deleted accounts
      accountIds: accounts.map((account) => account._id),
    },
    {
      onSubmit: (
        newPost: Post | Post[],
        previousPost: Post,
        scheduleAttemptId: string
      ) => {
        if (typeof onSubmit === "function") {
          return onSubmit?.(newPost);
        } else {
          return putResolve(
            savePost(newPost, previousPost, { scheduleAttemptId })
          );
        }
      },
    }
  );
}

function getAccountsByIds(ids: (string | undefined)[]) {
  return (state: ReduxRootState) =>
    ids.map((id) => state.entities.accounts.byId[id as string]).filter(Boolean);
}

function allAccountsSelector(state: ReduxRootState) {
  return state.entities.accounts.byId;
}

export async function processPostFormUpdate({
  post,
  formData,
  allAccounts,
  userTimezone,
  scheduleAttemptId,
}: {
  post: Post;
  formData: SaveFormResult;
  allAccounts: Record<string, AccountType>;
  userTimezone: string;
  scheduleAttemptId?: string;
}): Promise<{
  newPost: Post | Post[];
  previousPost: Post;
}> {
  const convertVideoCover = async (
    newPosts: Post | Post[],
    previousPost: Post
  ) => {
    const posts = Array.isArray(newPosts) ? newPosts : [newPosts];

    const promises = posts.map(async (post, index) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const {
        videoCoverPreview,
        videoCoverPreviewSource,
        videoCoverOriginal,
        videoCoverOriginalTimestamp,
        videoCoverOriginalType,
        ...postWithoutCoverPreview
      } = post;

      if (editingPostNeedUploadCover(post, previousPost)) {
        const upload = () => getUpdateThumbnail(post);
        return {
          ...postWithoutCoverPreview,
          thumbnailUrl: scheduleAttemptId
            ? await reuseAttemptCover(
                { attemptId: scheduleAttemptId, part: index },
                post,
                upload
              )
            : await upload(),
        } as Post;
      }

      // eslint-disable-next-line unicorn/no-useless-promise-resolve-reject
      return Promise.resolve(postWithoutCoverPreview as Post);
    });

    const results = await Promise.all(promises);

    return results.length > 1 ? results : results[0];
  };

  const previousPost = parseNgDataToPost(
    post as unknown as NgPost,
    allAccounts,
    formData.originalPostState,
    userTimezone
  );

  const newPost = await convertVideoCover(
    parseFormDataToPost(formData, allAccounts, userTimezone),
    previousPost
  );

  // Sync cover thumbnailUrl into mediaFiles[0] so downstream code paths
  // (savePost → createScheduledPost → getPostDataFromTypedPost) always
  // have both fields in agreement.
  const syncedPost = Array.isArray(newPost)
    ? newPost.map((post) =>
        normalizePostMediaShape(syncCoverToMediaFiles(post))
      )
    : normalizePostMediaShape(syncCoverToMediaFiles(newPost));

  return { newPost: syncedPost, previousPost };
}
